import React, { useRef, useState } from "react";
import styled from "styled-components";
import { TAP_TARGET } from "@/shared/theme/breakpoints";

/*
 * Phone view of PerformanceMetrics: the same metric tiles, four to a page (2×2), swiped sideways
 * with snap points and paged by dots. PerformanceMetrics renders the tiles; this only lays them out.
 */

const PAGE_SIZE = 4;

const Track = styled.div`
	display: flex;
	gap: 12px;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	overscroll-behavior-x: contain;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

const Page = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	/* Rows fit their cards; a short last page doesn't stretch its cards to the tallest page. */
	align-content: start;
	gap: 12px;
	flex: 0 0 100%;
	min-width: 0;
	scroll-snap-align: start;
	scroll-snap-stop: always;
`;

const Dots = styled.div`
	display: flex;
	justify-content: center;
`;

const Dot = styled.button<{ $active: boolean }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: ${TAP_TARGET}px;
	padding: 0;
	border: none;
	background: transparent;
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;

	&::before {
		content: "";
		width: ${({ $active }) => ($active ? 18 : 6)}px;
		height: 6px;
		border-radius: 3px;
		background: ${({ $active }) => ($active ? "#f72586" : "#c7cbe0")};
		transition:
			width 0.2s,
			background 0.2s;
	}

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: -8px;
		border-radius: 8px;
	}
`;

interface MetricsCarouselProps {
	tiles: React.ReactNode[];
}

export const MetricsCarousel: React.FC<MetricsCarouselProps> = ({ tiles }) => {
	const trackRef = useRef<HTMLDivElement>(null);
	const [page, setPage] = useState(0);
	const pages: React.ReactNode[][] = [];
	for (let start = 0; start < tiles.length; start += PAGE_SIZE) pages.push(tiles.slice(start, start + PAGE_SIZE));

	const onScroll = () => {
		const track = trackRef.current;
		if (!track || track.clientWidth === 0) return;
		setPage(Math.round(track.scrollLeft / track.clientWidth));
	};

	const goTo = (index: number) => {
		const track = trackRef.current;
		if (!track) return;
		track.scrollTo({ left: index * track.clientWidth, behavior: "smooth" });
		setPage(index);
	};

	return (
		<>
			<Track ref={trackRef} onScroll={onScroll} role="group" aria-roledescription="carousel" aria-label="Metrics">
				{pages.map((pageTiles, index) => (
					<Page
						key={index}
						role="group"
						aria-roledescription="slide"
						aria-label={`${index + 1} of ${pages.length}`}
					>
						{pageTiles}
					</Page>
				))}
			</Track>
			{pages.length > 1 && (
				<Dots>
					{pages.map((_, index) => (
						<Dot
							key={index}
							type="button"
							$active={index === page}
							aria-label={`Show metrics page ${index + 1}`}
							aria-current={index === page ? "true" : undefined}
							onClick={() => goTo(index)}
						/>
					))}
				</Dots>
			)}
		</>
	);
};
